import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { TopBar } from "../components/layout/TopBar";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Spinner } from "../components/shared/Spinner";
import { Button } from "../components/shared/Button";
import { SectionLabel } from "../components/shared/SectionLabel";
import * as ui from "../styles/ui";
import { api, type GitDocEntry } from "../api/client";
import { parseFrontMatter } from "../lib/front-matter";
import { formatApiError } from "../lib/api-errors";
import { useGitStore } from "../stores/useGitStore";
import { useBatchStore } from "../stores/useBatchStore";
import {
  runPublishChecks,
  hasBlockingFailures,
  type CheckLevel,
} from "../lib/publish-checks";
import { draftStakeholderNotice, noticeWindow } from "../lib/material-change";
import { versionSortKey } from "../../shared/versions";
import { websiteSlug } from "../../shared/doc-paths";
import {
  canPublish,
  type DocStatus,
  type SanityTargetType,
} from "../../shared/types";

interface PublishMetadata {
  title: string;
  version: string;
  status: DocStatus;
  docType: string;
  effectiveDate: string;
  familyPath: string;
  familySlug: string;
  repoPath: string;
  supersedes: string;
  notionPageId: string;
  materialChange: boolean;
  ticker: string;
  slug: string;
}

type PageState =
  | "loading"
  | "review"
  | "confirming"
  | "generating"
  | "publishing"
  | "success"
  | "error";

// Website Sanity content types the document can be published as. The public
// site reads these flat types (keyed by ticker), not the documentFamily records.
const TARGET_TYPE_OPTIONS: ReadonlyArray<[SanityTargetType, string]> = [
  ["methodology", "methodology"],
  ["factsheet", "factsheet"],
  ["pdfDocument", "PDF Document"],
];

function defaultTargetType(docType: string): SanityTargetType {
  if (docType === "factsheet") return "factsheet";
  if (docType === "pdfDocument") return "pdfDocument";
  return "methodology";
}

export function PublishPage() {
  const { family, version } = useParams<{ family: string; version: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // The editor passes the type selected in its sidebar so the review section
  // reflects it even when the new doc_type only lives on an unmerged PR branch
  // (the git read below would otherwise return the stale/old type).
  const navDocType = (location.state as { docType?: string } | null)?.docType;

  const [pageState, setPageState] = useState<PageState>("loading");
  const [meta, setMeta] = useState<PublishMetadata | null>(null);
  // Markdown body (front matter stripped) — this is what gets rendered into
  // the published document.
  const [content, setContent] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sanityUrl, setSanityUrl] = useState<string>("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfPreviewState, setPdfPreviewState] = useState<
    "idle" | "generating" | "ready" | "error"
  >("idle");
  const [pdfPreviewError, setPdfPreviewError] = useState("");
  const [generatingStep, setGeneratingStep] = useState<string>("");
  const [pdfMode, setPdfMode] = useState<string | null>(null);
  // Author-written change log. Starts empty and is only ever what the user
  // types — never auto-populated from a diff of the prior version.
  const [changeLog, setChangeLog] = useState("");
  // Material-change classification + the editable stakeholder notice. The
  // notice is regenerated from the draft helper until the user edits it.
  const [materialChange, setMaterialChange] = useState(false);
  const [noticeText, setNoticeText] = useState("");
  const [noticeEdited, setNoticeEdited] = useState(false);
  const [priorVersion, setPriorVersion] = useState<string | null>(null);
  // Website publish target + Index/Rate Ticker. Defaults from the doc's
  // template once metadata loads, but the user may override before publishing.
  const [targetType, setTargetType] = useState<SanityTargetType>("methodology");
  const [ticker, setTicker] = useState("");
  const fetchTree = useGitStore((s) => s.fetchTree);
  const treeDocs = useGitStore((s) => s.docs);
  const batchId = useBatchStore((s) => s.batchId);
  const stageInBatch = useBatchStore((s) => s.stage);
  const refreshBatch = useBatchStore((s) => s.refresh);
  const pdfBlobUrlRef = useRef<string | null>(null);

  // ── Load metadata from git tree + file content ──
  useEffect(() => {
    if (!family || !version) {
      setErrorMessage("Missing family or version in URL.");
      setPageState("error");
      return;
    }
    const fam = family;
    const ver = version;

    let cancelled = false;

    async function loadMetadata() {
      try {
        // Step 1: Find the doc entry in the git tree (default branch).
        const docs = await fetchTree();
        const entry = docs.find(
          (d: GitDocEntry) => d.family === fam && d.version === ver,
        );

        // Step 2: Prefer the batch draft branch when this doc is staged there,
        // so the review reflects the latest *saved but unmerged* edits (type,
        // status, content) instead of the stale default-branch version. This is
        // durable across refreshes, unlike router nav state.
        const targetPath = entry?.path ?? `${fam}/${ver}.md`;
        let stagedRef: string | undefined;
        try {
          const batch = await api.git.prStatus(batchId);
          if (batch.files.includes(targetPath)) stagedRef = batch.branch;
        } catch {
          // No active batch / offline — fall back to the default branch.
        }

        // Step 3: Read + parse the file (staged branch if staged, else default).
        try {
          const fileRes = await api.git.file(targetPath, stagedRef);
          const { data: fm, content: body } = parseFrontMatter(fileRes.content);
          const slug =
            (fm.family as string) ??
            entry?.family.split("/").pop() ??
            targetPath.split("/").slice(-2)[0] ??
            fam;

          if (!cancelled) {
            setContent(body);
            setMeta({
              title: (fm.title as string) ?? entry?.title ?? fam,
              version: (fm.version as string) ?? ver,
              status:
                (fm.status as DocStatus) ??
                (entry?.status as DocStatus) ??
                "draft",
              docType:
                navDocType ??
                (fm.doc_type as string) ??
                entry?.docType ??
                "methodology",
              effectiveDate: (fm.effective_date as string) ?? "",
              familyPath: entry?.family ?? fam,
              familySlug: slug,
              repoPath: targetPath,
              supersedes: (fm.supersedes as string) ?? "",
              notionPageId:
                (fm.notion_page_id as string) ?? entry?.notionPageId ?? "",
              materialChange: String(fm.material_change) === "true",
              ticker: (fm.ticker as string) ?? "",
              slug: ((fm.slug as string) ?? entry?.slug ?? "").trim(),
            });
            // Hydrate the toggle from persisted front matter; default off.
            setMaterialChange(String(fm.material_change) === "true");
            setPageState("review");
          }

          // Step 4: identify the prior version of this family (latest version
          // sorting below the current one) only to label the change log field.
          // The change log itself is author-written — never auto-generated from
          // a diff — so we never overwrite what the user typed.
          if (entry) {
            try {
              const currentKey = versionSortKey(ver);
              const prior = docs
                .filter(
                  (d: GitDocEntry) =>
                    d.family === entry.family &&
                    d.path !== entry.path &&
                    versionSortKey(d.version) >= 0 &&
                    (currentKey < 0 || versionSortKey(d.version) < currentKey),
                )
                .sort(
                  (a, b) =>
                    versionSortKey(b.version) - versionSortKey(a.version),
                )[0];
              if (prior && !cancelled) setPriorVersion(prior.version);
            } catch {
              // Non-fatal — the "vs <version>" label just stays hidden.
            }
          }
        } catch {
          // Fallback: tree entry data, or minimal metadata from URL params.
          if (!cancelled) {
            setMeta(
              entry
                ? {
                    title: entry.title,
                    version: ver,
                    status: (entry.status as DocStatus) ?? "draft",
                    docType: navDocType ?? entry.docType,
                    effectiveDate: "",
                    familyPath: entry.family,
                    familySlug: entry.family.split("/").pop() ?? entry.family,
                    repoPath: entry.path,
                    supersedes: "",
                    notionPageId: entry.notionPageId ?? "",
                    materialChange: false,
                    ticker: "",
                    slug: entry.slug ?? "",
                  }
                : {
                    title: fam,
                    version: ver,
                    status: "draft" as DocStatus,
                    docType: navDocType ?? "methodology",
                    effectiveDate: new Date().toISOString().split("T")[0],
                    familyPath: fam,
                    familySlug: fam.split("/").pop() ?? fam,
                    repoPath: "",
                    supersedes: "",
                    notionPageId: "",
                    materialChange: false,
                    ticker: "",
                    slug: "",
                  },
            );
            setPageState("review");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setErrorMessage(formatApiError(e));
          setPageState("error");
        }
      }
    }

    loadMetadata();
    return () => {
      cancelled = true;
    };
  }, [family, version, fetchTree, navDocType, batchId]);

  // Default the website publish target + ticker from the loaded document; the
  // user can still override either before publishing.
  useEffect(() => {
    if (!meta) return;
    setTargetType(defaultTargetType(meta.docType));
    setTicker(meta.ticker);
  }, [meta]);

  // ── Material-change notice window (announcement = today) ──
  const noticeWindowResult = useMemo(
    () =>
      noticeWindow({
        material: materialChange,
        effectiveDate: meta?.effectiveDate,
      }),
    [materialChange, meta?.effectiveDate],
  );

  // Keep the stakeholder notice in sync with the draft until the user edits it.
  useEffect(() => {
    if (!materialChange || noticeEdited || !meta) return;
    setNoticeText(
      draftStakeholderNotice({
        title: meta.title,
        version: meta.version,
        effectiveDate: meta.effectiveDate,
        changeLog,
      }),
    );
  }, [materialChange, noticeEdited, meta, changeLog]);

  // ── Pre-publish checks (deterministic, recomputed from current state) ──
  const checks = useMemo(() => {
    if (!meta) return [];
    return runPublishChecks({
      status: meta.status,
      title: meta.title,
      version: meta.version,
      family: meta.familyPath,
      docType: meta.docType,
      effectiveDate: meta.effectiveDate,
      body: content,
      docs: treeDocs,
      materialChange,
    });
  }, [meta, content, treeDocs, materialChange]);
  const checksBlocked = hasBlockingFailures(checks);

  const generatePdf = useCallback(async (): Promise<Blob | null> => {
    if (!meta) return null;
    setPdfPreviewState("generating");
    setPdfPreviewError("");

    try {
      const { response: pdfRes, mode } = await api.convert.pdf({
        // Render with the selected "publish as" type so the preview/cover
        // matches the target (methodology / factsheet / PDF), not just the
        // doc's stored type.
        template: targetType,
        title: meta.title,
        version: meta.version,
        content,
        metadata: {
          title: meta.title,
          version: meta.version,
          effectiveDate: meta.effectiveDate,
          status: meta.status,
          // The change log is git/release metadata, not document content — it
          // is deliberately omitted from the rendered PDF.
          ...(materialChange
            ? { materialChange: "true", materialNotice: noticeText.trim() }
            : {}),
        },
      });
      setPdfMode(mode);

      if (!pdfRes.ok) {
        throw new Error("PDF conversion failed.");
      }

      const blob = await pdfRes.blob();
      if (pdfBlobUrlRef.current) URL.revokeObjectURL(pdfBlobUrlRef.current);
      const url = URL.createObjectURL(blob);
      pdfBlobUrlRef.current = url;
      setPdfBlobUrl(url);
      setPdfPreviewState("ready");
      return blob;
    } catch (e) {
      const message = e instanceof Error ? e.message : "PDF generation failed.";
      setPdfPreviewError(message);
      setPdfPreviewState("error");
      return null;
    }
  }, [meta, content, materialChange, noticeText, targetType]);

  // Auto-generate the PDF preview when review data is ready; refresh on
  // material-change fields. Editing the change log does NOT re-render — it is
  // git/release metadata and never appears in the PDF.
  useEffect(() => {
    if (pageState !== "review" || !meta) return;
    const timer = setTimeout(() => {
      void generatePdf();
    }, 400);
    return () => clearTimeout(timer);
  }, [pageState, meta, content, materialChange, noticeText, generatePdf]);

  // ── Generate & Publish flow ──
  const handleGenerateAndPublish = useCallback(async () => {
    if (!meta || !family || !version) return;
    if (!canPublish(meta.status)) {
      setErrorMessage("Document must be in review status before publishing.");
      setPageState("error");
      return;
    }

    setPageState("generating");
    setGeneratingStep("Generating document...");

    try {
      const blob = await generatePdf();
      if (!blob) {
        throw new Error(pdfPreviewError || "PDF conversion failed.");
      }

      // Step 2: Version the rendered PDF in the git docs repo (non-fatal —
      // Sanity is the primary publish target, so a git failure shouldn't block).
      setGeneratingStep("Versioning PDF in git...");
      try {
        if (!meta.repoPath)
          throw new Error("Missing repo path for PDF commit.");
        const pdfPath = meta.repoPath.replace(/\.md$/, ".pdf");
        const pdfRes = await api.git.commitPdf({
          path: pdfPath,
          message: `publish: ${meta.title} ${meta.version} (PDF)`,
          pdf: blob,
          batch: batchId,
        });
        const pdfData = (await pdfRes.json().catch(() => null)) as {
          staged?: boolean;
        } | null;
        if (pdfData?.staged) stageInBatch(pdfPath);
      } catch {
        // Ignore — git versioning is best-effort here.
      }

      setGeneratingStep("Publishing to Sanity...");

      // Step 3: Publish to Sanity with the generated PDF
      const pdfFile = new File(
        [blob],
        `${meta.familySlug}-${meta.version}.pdf`,
        {
          type: "application/pdf",
        },
      );

      const sanityRes = await api.sanity.publish({
        familySlug: meta.familySlug,
        versionString: meta.version,
        pdfFile,
        title: meta.title,
        docType: meta.docType,
        targetType,
        ...(ticker.trim() ? { ticker: ticker.trim().toUpperCase() } : {}),
        ...(meta.slug.trim() ? { slug: meta.slug.trim() } : {}),
        effectiveDate: meta.effectiveDate,
        status: meta.status,
        // supersedes in front matter is a version string ("v0.1"); the worker
        // resolves it to a Sanity document ID within this family.
        ...(meta.supersedes ? { supersedesVersion: meta.supersedes } : {}),
        ...(changeLog.trim() ? { changeLog: changeLog.trim() } : {}),
        // Material-change fields ride the existing publish-metadata plumbing
        // (the worker reads them off the same JSON metadata blob). notice_date
        // is stamped today — the announcement moment is the publish moment.
        ...(materialChange
          ? {
              materialChange: true,
              noticeDate: new Date().toISOString().split("T")[0],
              ...(noticeText.trim() ? { notice: noticeText.trim() } : {}),
            }
          : {}),
        // Stage the git status→published change onto the active batch so it
        // joins the floating PR button.
        batch: batchId,
      });

      if (!sanityRes.ok) {
        throw new Error("Sanity publish failed.");
      }

      const sanityData = (await sanityRes.json()) as {
        cdnUrl?: string;
        publishStaged?: boolean;
        publishPath?: string;
      };
      setSanityUrl(sanityData.cdnUrl ?? "");
      if (sanityData.publishStaged && sanityData.publishPath) {
        stageInBatch(sanityData.publishPath);
      }
      void refreshBatch();
      setPageState("success");
    } catch (e) {
      setErrorMessage(
        e instanceof Error ? e.message : "Generate & Publish failed.",
      );
      setPageState("error");
    }
  }, [
    meta,
    family,
    version,
    changeLog,
    materialChange,
    noticeText,
    pdfPreviewError,
    generatePdf,
    targetType,
    ticker,
    batchId,
    stageInBatch,
    refreshBatch,
  ]);

  const handleCancel = useCallback(() => {
    navigate(`/family/${encodeURIComponent(family ?? "")}`);
  }, [navigate, family]);

  const handleBackToDashboard = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleConfirmPublish = useCallback(() => {
    setPageState("confirming");
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setPageState("review");
  }, []);

  // ── Cleanup blob URL on unmount ──
  useEffect(() => {
    return () => {
      if (pdfBlobUrlRef.current) URL.revokeObjectURL(pdfBlobUrlRef.current);
    };
  }, []);

  // ── Loading state ──
  if (pageState === "loading") {
    return (
      <>
        <TopBar title="loading..." />
        <div style={styles.centerWrap}>
          <Spinner size={24} label="loading…" />
        </div>
      </>
    );
  }

  // ── Error state ──
  if (pageState === "error") {
    return (
      <>
        <TopBar title="error" />
        <div style={styles.centerWrap}>
          <p style={styles.errorText}>{errorMessage}</p>
          <Button onClick={handleBackToDashboard}>dashboard</Button>
        </div>
      </>
    );
  }

  // ── Success state ──
  if (pageState === "success") {
    return (
      <>
        <TopBar title="published" />
        <div style={styles.centerWrap}>
          <div style={styles.successCard}>
            <span style={styles.successIcon}>&#10003;</span>
            <h2 style={styles.successTitle}>document published</h2>
            <p style={styles.successSubtext}>
              {meta?.title} {meta?.version} is now live on the website.
            </p>
            <div style={styles.cdnRow}>
              <label style={styles.cdnLabel}>sanity cdn url</label>
              <a
                href={sanityUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.cdnLink}
              >
                {sanityUrl}
              </a>
            </div>
            <Button onClick={handleBackToDashboard}>dashboard</Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar
        title={`publish — ${meta?.title ?? family} ${meta?.version ?? version}`}
        actions={<Button onClick={handleCancel}>back</Button>}
      />
      <div style={styles.pageWrap}>
        {/* Breadcrumb */}
        <nav style={styles.breadcrumb}>
          <button onClick={() => navigate("/")} style={styles.breadcrumbLink}>
            dashboard
          </button>
          <span style={styles.breadcrumbSep}>/</span>
          <button
            onClick={() =>
              navigate(`/family/${encodeURIComponent(family ?? "")}`)
            }
            style={styles.breadcrumbLink}
          >
            {meta?.title ?? family}
          </button>
          <span style={styles.breadcrumbSep}>/</span>
          <span style={styles.breadcrumbCurrent}>{version}</span>
        </nav>

        {/* ── Metadata Review ── */}
        <div style={styles.section}>
          <SectionLabel>metadata</SectionLabel>
          <div style={styles.metaGrid}>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>title</span>
              <span style={styles.metaValue}>{meta?.title ?? "—"}</span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>version</span>
              <span style={styles.metaValueMono}>
                {meta?.version ?? version}
              </span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>status</span>
              <StatusBadge status={meta?.status ?? "draft"} />
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>doc type</span>
              <span style={styles.metaValue}>{meta?.docType ?? "—"}</span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>effective date</span>
              <span style={styles.metaValue}>
                {meta?.effectiveDate
                  ? new Date(
                      meta.effectiveDate + "T00:00:00",
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "—"}
              </span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>family</span>
              <span style={styles.metaValueMono}>
                {meta?.familyPath ?? family}
              </span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>public url</span>
              <span style={styles.metaValueMono}>
                /static/
                {websiteSlug(meta?.slug, meta?.familySlug ?? family ?? "")}
              </span>
            </div>
            {meta?.supersedes && (
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>supersedes</span>
                <span style={styles.metaValueMono}>{meta.supersedes}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Publish Target (website Sanity type + ticker) ── */}
        <div style={styles.section}>
          <SectionLabel>publish as</SectionLabel>
          <div
            style={styles.segmented}
            role="group"
            aria-label="publish target type"
          >
            {TARGET_TYPE_OPTIONS.map(([value, label]) => {
              const active = targetType === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTargetType(value)}
                  style={{
                    ...styles.segmentBtn,
                    ...(active ? styles.segmentBtnActive : null),
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {targetType !== "pdfDocument" && (
            <div style={styles.tickerRow}>
              <label style={styles.metaLabel} htmlFor="publish-ticker">
                index/rate ticker
              </label>
              <input
                id="publish-ticker"
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="e.g. UPFI"
                spellCheck={false}
                style={styles.tickerInput}
              />
              <span style={styles.tickerHint}>
                the site finds{" "}
                {targetType === "factsheet" ? "factsheets" : "methodologies"} by
                ticker — leave blank to publish without one
              </span>
            </div>
          )}
        </div>

        {/* ── Pre-publish Checks ── */}
        <div style={styles.section}>
          <SectionLabel>checks</SectionLabel>
          <div style={styles.checkList}>
            {checks.map((check, i) => (
              <div key={i} style={styles.checkLine}>
                <span
                  style={{
                    ...styles.checkMark,
                    color: CHECK_COLORS[check.level],
                  }}
                >
                  {CHECK_MARKS[check.level]}
                </span>
                <span style={styles.checkText}>{check.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Change Log ── */}
        <div style={styles.section}>
          <SectionLabel>change log</SectionLabel>
          <div style={styles.changeLogMeta}>
            {priorVersion ? `vs ${priorVersion}` : "initial version"}
          </div>
          <textarea
            value={changeLog}
            onChange={(e) => setChangeLog(e.target.value)}
            spellCheck={false}
            style={styles.changeLogArea}
          />
        </div>

        {/* ── Material Change ── */}
        <div style={styles.section}>
          <SectionLabel>material change</SectionLabel>
          <div
            style={styles.segmented}
            role="group"
            aria-label="material change"
          >
            {(
              [
                ["non-material", false],
                ["material", true],
              ] as const
            ).map(([label, value]) => {
              const active = materialChange === value;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setMaterialChange(value)}
                  style={{
                    ...styles.segmentBtn,
                    ...(active ? styles.segmentBtnActive : null),
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {materialChange && (
            <div style={styles.noticeWrap}>
              <SectionLabel>stakeholder notice</SectionLabel>
              <textarea
                value={noticeText}
                onChange={(e) => {
                  setNoticeText(e.target.value);
                  setNoticeEdited(true);
                }}
                spellCheck={false}
                style={styles.changeLogArea}
              />
              <div
                style={{
                  ...styles.changeLogMeta,
                  ...(noticeWindowResult.ok ? null : styles.noticeShort),
                  marginTop: 4,
                }}
              >
                {noticeWindowResult.reason}
              </div>
            </div>
          )}
        </div>

        {/* ── PDF Preview Area ── */}
        <div style={styles.section}>
          <SectionLabel>pdf preview</SectionLabel>
          {pdfBlobUrl && pdfPreviewState === "ready" ? (
            <div style={styles.pdfPreviewWrap}>
              <iframe
                src={pdfBlobUrl}
                style={styles.pdfIframe}
                title="PDF Preview"
              />
            </div>
          ) : pdfPreviewState === "error" ? (
            <div style={styles.pdfPlaceholder}>
              <p style={styles.pdfPlaceholderText}>pdf generation failed</p>
              <p style={styles.pdfPlaceholderSubtext}>{pdfPreviewError}</p>
            </div>
          ) : (
            <div style={styles.pdfPlaceholder}>
              <Spinner size={20} label="generating pdf…" />
            </div>
          )}
        </div>

        {pdfMode === "docx-placeholder" && (
          <div style={styles.pdfWarn}>
            PDF mode: placeholder (DOCX bytes). Enable browser binding in
            wrangler for real PDFs.
          </div>
        )}

        {/* ── Publish Actions ── */}
        {meta?.effectiveDate &&
          meta.effectiveDate > new Date().toISOString().split("T")[0] && (
            <div style={styles.effectiveLine}>
              effective from {meta.effectiveDate}
            </div>
          )}
        {checksBlocked && (
          <div style={styles.blockedNote}>
            resolve blocking checks before publishing
          </div>
        )}
        <div style={styles.actionsRow}>
          <Button onClick={handleCancel}>cancel</Button>
          <Button
            variant="primary"
            onClick={handleConfirmPublish}
            disabled={
              pageState === "generating" ||
              pageState === "publishing" ||
              pageState === "confirming" ||
              pdfPreviewState === "generating" ||
              (meta != null && !canPublish(meta.status)) ||
              checksBlocked
            }
          >
            generate &amp; publish
          </Button>
        </div>

        {/* ── Generating progress ── */}
        {(pageState === "generating" || pageState === "publishing") && (
          <div style={styles.progressCard}>
            <Spinner size={20} label={generatingStep} />
          </div>
        )}
      </div>

      {/* ── Confirmation Modal ── */}
      {pageState === "confirming" && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.card}>
            <h3 style={modalStyles.heading}>confirm publish</h3>
            <p style={modalStyles.body}>
              Are you sure? This will publish to Sanity and make the document
              live on the website.
            </p>
            <div style={modalStyles.btnRow}>
              <Button onClick={handleConfirmCancel}>cancel</Button>
              <Button variant="primary" onClick={handleGenerateAndPublish}>
                confirm &amp; publish
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Check markers ──

const CHECK_MARKS: Record<CheckLevel, string> = {
  pass: "✓",
  warn: "!",
  fail: "✗",
};

const CHECK_COLORS: Record<CheckLevel, string> = {
  pass: "var(--fg-3)",
  warn: "var(--fg-3)",
  fail: "var(--fg-3)",
};

// ── Page styles ──

const styles: Record<string, React.CSSProperties> = {
  centerWrap: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  errorText: {
    color: "var(--comp-salmon)",
    fontSize: 13,
    marginBottom: "var(--space-4)",
  },
  pageWrap: {
    ...ui.pageContent,
    maxWidth: 800,
  },
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 16,
    fontSize: 12,
  },
  breadcrumbLink: {
    background: "none",
    border: "none",
    color: "var(--fg-3)",
    cursor: "pointer",
    padding: 0,
    fontSize: 12,
    textDecoration: "none",
  },
  breadcrumbSep: {
    color: "var(--fg-3)",
    fontSize: 12,
    opacity: 0.5,
  },
  breadcrumbCurrent: {
    color: "var(--fg-1)",
    fontSize: 12,
    fontWeight: 600,
  },
  section: {
    marginBottom: "var(--space-5)",
  },
  metaGrid: {
    display: "grid" as const,
    gridTemplateColumns: "1fr 1fr",
    gap: "8px 20px",
  },
  metaItem: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: 3,
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: "var(--fg-3)",
    letterSpacing: "0.08em",
    textTransform: "lowercase" as const,
  },
  metaValue: {
    fontSize: 13,
    color: "var(--fg-1)",
  },
  metaValueMono: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
  },
  pdfPreviewWrap: {
    width: "100%",
    height: 480,
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
    background: "var(--comp-hover)",
  },
  pdfIframe: {
    width: "100%",
    height: "100%",
    border: "none",
  },
  pdfPlaceholder: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    height: 200,
    border: "1px dashed var(--comp-rule)",
    borderRadius: "var(--radius-md)",
    background: "var(--comp-hover)",
  },
  pdfPlaceholderText: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-3)",
    margin: "0 0 4px",
  },
  pdfPlaceholderSubtext: {
    fontSize: 12,
    color: "var(--fg-3)",
    margin: 0,
    opacity: 0.7,
  },
  checkList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
  },
  checkLine: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
  },
  checkMark: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    width: 12,
    flexShrink: 0,
    opacity: 0.85,
  },
  checkText: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
  },
  changeLogMeta: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    marginBottom: 4,
  },
  changeLogArea: {
    width: "100%",
    minHeight: 72,
    padding: "6px 0",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    borderRadius: 0,
    fontSize: 12,
    lineHeight: 1.6,
    fontFamily: "var(--font-mono)",
    background: "transparent",
    color: "var(--fg-1)",
    outline: "none",
    resize: "vertical" as const,
  },
  segmented: {
    display: "inline-flex",
    gap: 18,
  },
  segmentBtn: {
    background: "none",
    border: "none",
    padding: "2px 0",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--fg-3)",
    cursor: "pointer",
    borderBottom: "1px solid transparent",
  },
  segmentBtnActive: {
    color: "var(--fg-1)",
    borderBottom: "1px solid var(--fg-1)",
  },
  tickerRow: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
    marginTop: "var(--space-3)",
    maxWidth: 280,
  },
  tickerInput: {
    width: "100%",
    padding: "6px 0",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    borderRadius: 0,
    fontSize: 13,
    fontFamily: "var(--font-mono)",
    letterSpacing: "0.04em",
    background: "transparent",
    color: "var(--fg-1)",
    outline: "none",
  },
  tickerHint: {
    fontSize: 11,
    color: "var(--fg-3)",
  },
  noticeWrap: {
    marginTop: "var(--space-3)",
  },
  noticeShort: {
    color: "var(--comp-mustard)",
  },
  blockedNote: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    textAlign: "right" as const,
    marginBottom: "var(--space-2)",
  },
  pdfWarn: {
    marginBottom: 16,
    fontSize: 12,
    color: "var(--comp-salmon)",
    fontFamily: "var(--font-mono)",
  },
  effectiveLine: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    textAlign: "right" as const,
    marginBottom: "var(--space-2)",
  },
  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "var(--space-2)",
    marginBottom: "var(--space-5)",
  },
  progressCard: {
    padding: "var(--space-4) 0",
    marginBottom: "var(--space-5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--fg-2)",
  },
  // ── Success state ──
  successCard: {
    padding: "var(--space-5)",
    textAlign: "center" as const,
    maxWidth: 480,
  },
  successIcon: {
    display: "block",
    fontSize: 16,
    fontWeight: 700,
    color: "var(--comp-green)",
    marginBottom: 8,
  },
  successTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-1)",
    margin: "0 0 8px",
  },
  successSubtext: {
    fontSize: 13,
    color: "var(--fg-3)",
    margin: "0 0 16px",
  },
  cdnRow: {
    marginBottom: "var(--space-4)",
    textAlign: "left" as const,
  },
  cdnLabel: {
    display: "block",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "lowercase" as const,
    color: "var(--fg-3)",
    marginBottom: "var(--space-1)",
  },
  cdnLink: {
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    color: "var(--fg-1)",
    wordBreak: "break-all" as const,
    textDecoration: "underline",
  },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "var(--overlay-deep)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  card: {
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-4)",
    width: 400,
    maxWidth: "90vw",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  },
  heading: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-1)",
    margin: "0 0 var(--space-3)",
  },
  body: {
    fontSize: 13,
    color: "var(--fg-2)",
    lineHeight: 1.6,
    margin: "0 0 var(--space-4)",
  },
  btnRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "var(--space-2)",
  },
};
